import apiClient from '@/utils/cb-client'
import HostedPageHelper from '@/utils/hosted-page-helper'
import { EventBus } from '@/utils/event-bus'
import cardFormatter from "@/utils/card-formatter"
/**
 * CheckoutFormLogger
 * 
 * author: Sivanantham G (siva@chargebee.com)
 */

const sections = [
    {
        name: 'customer',
        store_data: (store) => store.state.customer.data
    },
    {
        name: 'subscription',
        store_data: (store) => store.state.order.subscription
    },
    {
        name: 'billing_address',
        store_data: (store) => store.state.customer.billing_address
    },
    {
        name: 'shipping_address',
        store_data: (store) => store.state.order.shipping_address,
        store_data_field_proxy: (store, field) => {
            return {
                'shipping.ship.to.billing': store.state.order.ship_to_billing_enabled
            }[field];
        }
    }
];

class CheckoutFormLogger{

    constructor(){}

    precheck(){
        this.stop();
    }

    start(pStore, config){
        this.config = Object.assign({
            enabled: false,
            interval_delay: 5000
        }, config);
        this.precheck();
        if(this.config.enabled){
            this.store = pStore;
            this._lazzyStart();
        }
    }

    stop(){
        if(this._looper){
            clearInterval(this._looper);
        }
        this._stopListening();
    }

    /**
     * Client Logs
     */

    _lazzyStart(){
        if(this.store.getters.checkout_config_loaded){
            this._init();
            this._looper = setInterval(()=>this._loop(), this.config.interval_delay);
            this._startListening();
        }else{
            setTimeout(()=>this._lazzyStart(), 100);
        }
    }

    _init(){
        this.sections = sections.filter((s) => this._getFieldsConfig(s).length > 0);
        this.sections.forEach((section)=>{
             var lfn = this._getLocalDataFormName(section);
             this.store.dispatch('create_form', {
                 name: lfn,
                 config: this._getFieldsConfig(section)
             });
         });
     }

    _loop(forceRun){
        if(this._compare() || forceRun){
            let client = {'user_agent': navigator.userAgent};
            client['fields'] = this.sections.reduce((acc, section) => {
                let fields = this._getFormDataByName(this._getLocalDataFormName(section));
                Object.keys(fields).forEach((key) => {
                    let template = (key != 'shipping.ship.to.billing') ? `${section.name}[${key}]` : `${key}`;
                    acc[template] = {value: fields[key].value, optional: !fields[key].meta.required};
                });
                return acc;
            }, {});
            this._sendLog(client, 'client');
        }
    }

    _compare(){
        let result = false;
        this.sections.forEach((section)=>{
            let lfn = this._getLocalDataFormName(section);
            let localData = this.store.getters.values(lfn);
            this.store.getters.fields(lfn).forEach((fieldName) => {
                let val = this._fetchValue(section, fieldName)
                if(localData[fieldName] != val){
                    this.store.dispatch('update_form_input',{
                        formName: lfn,
                        name: fieldName,
                        value: val
                    });
                    result = true;
                }        
            });
        });
        return result;
    }

    _getLocalDataFormName(pSection){
        return "logger_" + pSection.name;
    }

    _getFieldsConfig(pSection){
        return this.store.getters.checkout_sections[pSection.name].fields
    }

    _getFormDataByName(pSectionName){
        return this.store.state.form[pSectionName] && this.store.state.form[pSectionName].data
    }

    _fetchValue(pSection, pFieldName){
        if((pSection.name == this.store.getters.current_checkout_step) && !(this.store.getters.is_checkout_gift && pSection.name == "customer")) {
            return this._formValue(pSection, pFieldName);
        }
        return this._storeValue(pSection, pFieldName) || undefined;
    }

    _storeValue(pSection, pFieldName){
        return (pSection.store_data_field_proxy && pSection.store_data_field_proxy(this.store, pFieldName))
                || (pSection.store_data(this.store) && pSection.store_data(this.store)[pFieldName]);
    }

    _formValue(pSection, pFieldName){
      const formData = this._getFormDataByName(pSection.name);
      const fieldData = formData && formData[pFieldName];
      return (fieldData && !fieldData.invalid &&  fieldData.value) || "";
    }

    _sendLog(data, type){
        apiClient.loggers.hp_form(
            {
                hp_token: this.store.state.auth.hpToken,
                log_type: type
            }, 
            data
        ).then().catch((error) => logger.e(error));
    }

    /**
     * server log
     */

    _startListening(){
        EventBus.$on('hp_log', d => {
            try {
                switch (d.action) {
                  case 'init':
                    this._sendLog(this._getInitialDetails(), 'server');
                    this._loop(true); // initial empty client log
                    break;
                  case 'coupon_applied':
                  case 'coupon_removed':
                  case 'coupon_failed':
                    this._sendLog(this._getCouponDetails(d), 'server');
                    break;
                  case 'checkout_submitted':
                  case 'checkout_failed':
                    this._sendLog(this._getCheckoutDetails(d), 'server');
                    break;
                }
            } catch (error) {
              //logger.e(error);
            }
        });
    }

    _stopListening(){
        EventBus.$off('hp_log');
    }

    _getInitialDetails(){
        let o = this._getOrderDetails()
        if(this.store.getters.is_checkout_existing){
            o['subscription'] = {'id': this.store.getters.subscription_handle};
        }
        return o;
    }
      
    _getOrderDetails(){
        //check if amount_due has to be used?
        return {
            'order_value': this.store.getters.estimate_total, 
            'currency':  this.store.getters.currency_code
        }; 
    }

    _getCouponDetails(d){
        var log = {}, coupon_actions = {timestamp: HostedPageHelper.getCurrentTimestamp()};
        if(d.action == 'coupon_applied' && d.code){
          coupon_actions['status'] = 'succeeded'
          coupon_actions['code'] = d.code;
        }else if(d.action == 'coupon_removed'){
          coupon_actions['status'] = 'removed';
        }else if(d.action == 'coupon_failed' && d.code && d.error){
          log['failed'] = 1;
          coupon_actions['status'] = 'failed';
          coupon_actions['code'] = d.code;
          coupon_actions['failure_msg'] = d.error._source['error_msg']; 
          coupon_actions['api_error_code'] = d.error._source['api_error_code']; 
          coupon_actions['error_code'] = d.error._source['error_code'];
        }else{
          throw new TypeError('hp_server_log: undefined coupon action invoked.')
        }
        log['coupon_actions'] = coupon_actions;
        return Object.assign(log, this._getOrderDetails());
    }

    /**
     * In App Side, Supported CardType ENUM
     * VISA(100),MASTERCARD(200),AMERICAN_EXPRESS(300),DISCOVER(400),JCB(500),DINERS_CLUB(600),OTHER(999);
     */
    _getCardType(type) {
        switch (type) {
            case "visa":
            case "mastercard":
            case "american_express":
            case "discover":
            case "jcb":
            case "diners_club":
                return type;
            case "dinersclub":
                return "diners_club";
            case "amex":
                return "american_express";
            default:
                return "other";
        }
    }

    /**
     * In App Side, Supported PaymentMethod ENUM
     *  CARD(10),PAYPAL_EXPRESS_CHECKOUT(20),AMAZON_PAYMENTS(30),BANK_ACCOUNT(40),DIRECT_DEBIT(50),APPLE_PAY(60),OTHER(100);
     */
     _getPaymentMethod(method) {
         switch (method){
            case "card":
            case "paypal_express_checkout":
            case "amazon_payments":
            case "bank_account":
            case "direct_debit":
            case "apple_pay":
                return method;
            default:
                return "other";
         }
     }

    _getCheckoutDetails(d){
        let log = {} , checkout_submit = {timestamp: HostedPageHelper.getCurrentTimestamp()};
        if(this.store.getters.selected_payment_method){ // for trial subscription feature.
            var gateway = this.store.getters.pm_list.filter(pm => pm.id == this.store.getters.selected_payment_method.gateway_account_id)[0];
            checkout_submit['gateway'] = {
              id: this.store.getters.selected_payment_method.gateway_account_id,
              name: gateway.gateway_name
            };
            checkout_submit['payment_method'] = this._getPaymentMethod(this.store.getters.selected_payment_method.type);
            if(this.store.getters.selected_payment_method.type == "card") {
                if(this.store.getters.selected_payment_method.card) {
                    checkout_submit['card_type'] = this._getCardType(this.store.getters.selected_payment_method.card.brand);
                }
                else {
                    checkout_submit['card_type'] = this._getCardType(cardFormatter.cardType(this.store.getters.selected_payment_method.number));
                }
            }
        }
        if(d.action == 'checkout_submitted'){
          checkout_submit['status'] = 'succeeded';
        }else if(d.action == 'checkout_failed' && d.error){
          log['failed'] = 1;
          checkout_submit['status'] = 'failed';
          checkout_submit['errors'] = {
            error_msg: d.error._source.error_msg,
            api_error_code: d.error._source.api_error_code,
            error_code: d.error._source.error_code,
            http_status_code: d.error._source.http_status_code
          };
        }else{
          throw new TypeError('hp_server_log: undefined checkout submit action invoked.')
        }
        log['checkout_submit'] = checkout_submit;
        return log;
      }

}

export const checkoutFormLogger = new CheckoutFormLogger();